import { User } from '../types';
import request from './request';

const getWithRetry = async (url: string, config?: any) => {
  const max = parseInt(process.env.REACT_APP_API_RETRY_MAX_ATTEMPTS || '3', 10);
  const base = parseInt(process.env.REACT_APP_API_RETRY_BASE_MS || '500', 10);
  const cap = parseInt(process.env.REACT_APP_API_RETRY_MAX_DELAY_MS || '5000', 10);
  let attempt = 0;
  let lastErr: any;
  while (attempt < max) {
    try {
      return await request.get(url, config);
    } catch (err) {
      lastErr = err;
      attempt++;
      const delay = Math.min(cap, base * Math.pow(2, attempt - 1));
      await new Promise((res) => setTimeout(res, delay));
    }
  }
  throw lastErr;
};

// Fetch users with pagination (mocking pagination for now as per backend)
export const getUsersApi = async (): Promise<User[]> => {
  const response = await getWithRetry('/api/users');
  const data = Array.isArray(response.data)
    ? response.data
    : (response.data?.users || response.data?.data || []);
  return data.map((u: any) => ({
    id: u.id,
    name: u.username,
    phone: u.phone_number,
    avatar:
      u.avatar ||
      `https://ui-avatars.com/api/?name=${encodeURIComponent(
        u.username || u.phone_number
      )}&background=random`,
    tags: (u.tags || []).map((t: any) => String(t.id)),
    unreadCount: u.unreadCount || 0,
  }));
};

// Search users with pagination
export const searchUsersApi = async (
  term: string,
  tags: string[],
  offset: number = 0,
  limit: number = 20
): Promise<User[]> => {
  const usp = new URLSearchParams();
  if (term) usp.set('q', term);
  tags.forEach(t => usp.append('tags', String(t)));
  usp.set('offset', String(offset));
  usp.set('limit', String(limit));

  const url = `/api/users/search?${usp.toString()}`;
  const response = await request.get(url);
  const data = Array.isArray(response.data) ? response.data : (response.data?.users || response.data?.data || []);

  return data.map((u: any) => ({
    id: u.id,
    name: u.username,
    phone: u.phone_number,
    avatar: u.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(u.username || u.phone_number)}&background=random`,
    tags: (u.tags || []).map((t: any) => String(t.id)),
    lastMessage: u.lastMessage,
    highlights: u.highlights || [],
    unreadCount: u.unreadCount || 0,
  }));
};

// Update user tags
export const updateUserTagsApi = async (userId: string, tagIds: number[]): Promise<User> => {
  const payloadTagIds = (tagIds || [])
    .map((id) => {
      const n = typeof id === 'number' ? id : parseInt(id as any, 10);
      return Number.isNaN(n) ? null : n;
    })
    .filter((v) => v !== null);
  const response = await request.put(`/api/users/${userId}/tags`, { tagIds: payloadTagIds });
  const u = response.data;
  return {
    id: u.id,
    name: u.username,
    phone: u.phone_number,
    avatar: u.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(u.username || u.phone_number)}&background=random`,
    tags: (u.tags || []).map((t: any) => String(t.id)),
  };
};
